<template>
    <div class="ui-page">
        <ui-page-header title="Prazos de TCA"
            subtitle="Toda TCA em acompanhamento tem o prazo de 30 dias corridos para a RJ. A plataforma envia os lembretes da régua e cobra a resposta do dono do caso.">
            <template #title-append>
                <ui-badge v-if="settings" :status="settings.reminders_enabled ? 'active' : 'inactive'" :text="settings.reminders_enabled ? 'Régua ligada' : 'Régua desligada'" />
            </template>
            <!-- Atalho para importar a planilha (RecuperaJur ou BRZJ) com as TCAs -->
            <v-btn v-if="$allowed('prospect.import')" outlined @click="$router.push({ path: '/prospeccao/casos', query: { aba: 'importacoes' } })"><v-icon left small>mdi-table-arrow-up</v-icon>Importar</v-btn>
            <v-btn outlined :disabled="!data || !data.open.length" @click="exportRows"><v-icon left small>mdi-microsoft-excel</v-icon>Exportar (.xlsx)</v-btn>
            <v-btn color="primary" depressed @click="openSettings"><v-icon left small>mdi-cog-outline</v-icon>Configurar régua</v-btn>
        </ui-page-header>

        <section v-if="data" class="pdl-stats mb-5" aria-label="Indicadores das cautelares">
            <div v-for="kpi in kpis" :key="kpi.label" class="pdl-stat">
                <span class="pdl-stat__label"><span v-if="kpi.tone" class="ui-pill__dot" :class="`ui-pill__dot--${kpi.tone}`"></span>{{ kpi.label }}</span>
                <span class="pdl-stat__value">{{ kpi.value }}</span>
                <span class="pdl-stat__hint">{{ kpi.hint }}</span>
            </div>
        </section>

        <div v-if="settings && !settings.reminders_enabled" class="pdl-notice mb-4">
            <v-icon small>mdi-email-off-outline</v-icon>
            <span>A régua está desligada: os prazos são calculados, mas nenhum e-mail é enviado. Cadastre os destinatários e ligue em "Configurar régua".</span>
        </div>

        <ui-section title="Linha do tempo dos prazos" :description="`Próximas 8 semanas a partir de ${formatFeedDate(weeks[0])}. Vermelho: até 3 dias ou vencido · amarelo: até 20 dias · lilás: mais de 20 dias.`">
            <ui-empty v-if="data && !data.open.length" text="Nenhuma TCA com prazo aberto." />
            <div v-else class="pdl-timeline">
                <div class="pdl-timeline__scale">
                    <span v-for="week in weeks" :key="week">{{ formatShort(week) }}</span>
                </div>
                <button v-for="item in timelineItems" :key="item.id" type="button" class="pdl-row" @click="openCase(item.case.id)">
                    <span class="pdl-row__name">
                        <strong>{{ item.case.company_name }}</strong>
                        <span class="ui-muted"> · {{ item.case.uf || "—" }} · {{ formatMoneyShort(item.case.amount) }}</span>
                    </span>
                    <span class="pdl-row__track">
                        <span class="pdl-row__bar" :class="`pdl-row__bar--${item.tone}`" :style="{ width: `${item.position}%` }"></span>
                        <span class="pdl-row__label" :class="`pdl-row__label--${item.tone}`">
                            {{ formatShort(item.due_date) }} · {{ item.days_text }}<span v-if="item.estimated"> · estimado</span><span v-if="item.reminders_paused"> · pausado</span>
                        </span>
                    </span>
                </button>
            </div>
        </ui-section>

        <div class="pdl-grid mt-5">
            <ui-section title="Régua de lembretes" description="Cada marco é enviado uma vez por vencimento; prorrogar o prazo recomeça a régua.">
                <table class="pdl-rule">
                    <tbody>
                        <tr v-for="rule in rules" :key="rule.key">
                            <td class="pdl-rule__key">{{ rule.label }}</td>
                            <td>{{ rule.text }}<div class="ui-small ui-muted">{{ rule.recipients }}</div></td>
                        </tr>
                    </tbody>
                </table>
                <p v-if="settings" class="ui-small ui-muted mt-3 mb-0">
                    Envio a partir das {{ settings.reminder_hour }}h · equipe: {{ settings.team_emails.join(", ") || "não cadastrada" }} · gestão: {{ settings.management_emails.join(", ") || "não cadastrada" }}.
                    Sem dono, a gestão recebe todos os marcos.
                </p>
            </ui-section>

            <ui-section title="Respostas pendentes" description="Lembretes enviados que ainda não foram respondidos pelo link do e-mail.">
                <ui-empty v-if="data && !data.pending_replies.length" text="Nenhuma resposta pendente." />
                <table v-else class="pdl-replies">
                    <tbody>
                        <tr v-for="reply in data ? data.pending_replies : []" :key="reply.id" @click="openCase(reply.case.id)">
                            <td><strong>{{ reply.case.company_name }}</strong><div class="ui-small ui-muted">{{ reply.case.uf || "—" }}</div></td>
                            <td class="ui-nowrap">{{ reply.milestone_label }}</td>
                            <td class="ui-nowrap">{{ formatDateTime(reply.sent_at) }}</td>
                            <td>{{ reply.case.owner ? reply.case.owner.name : "sem dono" }}</td>
                            <td class="ui-nowrap pdl-late">sem resposta · {{ daysSince(reply.sent_at) }} d</td>
                        </tr>
                    </tbody>
                </table>
            </ui-section>
        </div>

        <ui-section v-if="data && data.closed.length" title="Encerradas recentemente" class="mt-5">
            <table class="pdl-replies">
                <tbody>
                    <tr v-for="item in data.closed" :key="item.id" @click="openCase(item.case.id)">
                        <td><strong>{{ item.case.company_name }}</strong><div class="ui-small ui-muted">{{ item.case.uf || "—" }}</div></td>
                        <td>{{ item.outcome_label }}<span v-if="item.rj_process_number" class="ui-mono ui-small"> · {{ item.rj_process_number }}</span></td>
                        <td class="ui-nowrap">vencimento {{ formatFeedDate(item.due_date) }}</td>
                    </tr>
                </tbody>
            </table>
        </ui-section>

        <v-dialog v-model="config.open" max-width="600" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Configurar régua</h3>
                        <p class="ui-section-description">O dono do caso recebe sempre. A equipe de prospecção recebe todos os marcos; a gestão, D−7 (cópia), D0, D+3 e os casos sem dono.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-switch v-model="config.form.reminders_enabled" inset dense hide-details label="Enviar lembretes por e-mail" class="mt-0 mb-4"></v-switch>
                    <v-row dense>
                        <v-col cols="6"><v-text-field v-model="config.form.tca_days" type="number" min="1" label="Prazo-base da TCA (dias corridos)" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="6"><v-select v-model="config.form.reminder_hour" :items="hourItems" label="Horário de envio" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="12"><v-autocomplete v-model="config.form.team_emails" :items="recipientItems" label="Equipe de prospecção" placeholder="Escolha os administradores" multiple chips small-chips deletable-chips outlined dense hide-details="auto"
                            :loading="config.loadingAdmins" :menu-props="{ offsetY: true }" no-data-text="Nenhum administrador encontrado"><template #item="{ item, attrs }"><v-list-item-action class="mr-3"><v-icon :color="attrs.inputValue ? 'primary' : ''">{{ attrs.inputValue ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}</v-icon></v-list-item-action><v-list-item-content><v-list-item-title>{{ item.text }}</v-list-item-title><v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle></v-list-item-content></template></v-autocomplete></v-col>
                        <v-col cols="12"><v-autocomplete v-model="config.form.management_emails" :items="recipientItems" label="Gestão" placeholder="Escolha os administradores" multiple chips small-chips deletable-chips outlined dense hide-details="auto"
                            :loading="config.loadingAdmins" :menu-props="{ offsetY: true }" no-data-text="Nenhum administrador encontrado"><template #item="{ item, attrs }"><v-list-item-action class="mr-3"><v-icon :color="attrs.inputValue ? 'primary' : ''">{{ attrs.inputValue ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}</v-icon></v-list-item-action><v-list-item-content><v-list-item-title>{{ item.text }}</v-list-item-title><v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle></v-list-item-content></template></v-autocomplete></v-col>
                    </v-row>
                    <p class="ui-small ui-muted mt-3 mb-0">O prazo-base vale para os novos prazos estimados; os já lançados não mudam. O resumo semanal sai às sextas no mesmo horário.</p>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="config.saving" @click="config.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('prospect.settings')" color="primary" depressed :loading="config.saving" @click="saveSettings">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import { downloadXlsx } from "@/common/util/export-xlsx.util";
import { daysSince, formatDateTime, formatFeedDate, formatMoneyShort, notifyError, notifySuccess, todayKey } from "@/components/prospect/prospect.util";

const RULES = [
    { key: "d-15", label: "D−15", text: "Aviso: prazo da TCA em 15 dias. Pede atualização do status BeJ.", recipients: "dono do caso, equipe" },
    { key: "d-7", label: "D−7", text: "Cobrança: há agenda marcada? Onepage enviado? Resposta em 1 dia útil.", recipients: "dono do caso, equipe, cópia à gestão" },
    { key: "d-2", label: "D−2", text: "Conferir no Whom se a RJ foi distribuída ou se houve prorrogação.", recipients: "dono do caso, equipe" },
    { key: "d0", label: "D0", text: "Prazo vence hoje. Pede o registro do desfecho (RJ distribuída, prorrogação ou sem RJ).", recipients: "dono, equipe, gestão" },
    { key: "d+3", label: "D+3", text: "Sem desfecho registrado: escalonamento à gestão.", recipients: "equipe, gestão" },
    { key: "weekly", label: "Sexta", text: "Resumo semanal: todas as TCAs abertas, por vencimento, com a última resposta de cada dono.", recipients: "squad inteiro" },
];

function addDays(date, days) {
    const value = new Date(`${date}T12:00:00Z`);
    value.setUTCDate(value.getUTCDate() + days);
    return value.toISOString().slice(0, 10);
}

export default {
    name: "ProspectDeadlines",

    data: () => ({
        data: null,
        settings: null,
        loading: false,
        rules: RULES,
        hourItems: Array.from({ length: 16 }, (_, index) => ({ value: index + 6, text: `${index + 6}h` })),
        config: { open: false, saving: false, loadingAdmins: false, form: {} },
        admins: [],
    }),

    computed: {
        // Administradores pelo nome (o valor salvo e o e-mail). E-mail salvo que
        // nao e de nenhum administrador continua na lista para poder ser tirado.
        recipientItems() {
            const items = this.admins.map((person) => ({ value: person.email, text: person.name || person.email, email: person.email }));
            const form = this.config.form || {};
            const known = new Set(items.map((item) => item.value));
            [...(form.team_emails || []), ...(form.management_emails || [])].forEach((email) => {
                if (known.has(email)) return;
                known.add(email);
                items.push({ value: email, text: email, email: "não é administrador" });
            });
            return items;
        },
        weeks() {
            const start = this.data ? this.data.today : todayKey();
            return Array.from({ length: 8 }, (_, index) => addDays(start, index * 7));
        },
        timelineItems() {
            if (!this.data) return [];
            const total = 56;
            return this.data.open.map((item) => ({
                ...item,
                position: Math.max(2, Math.min(100, ((item.days_left + 0.5) / total) * 100)),
            }));
        },
        kpis() {
            const kpis = this.data ? this.data.kpis : {};
            return [
                { label: "TCAs com prazo aberto", value: kpis.open || 0, hint: "todas as UFs" },
                { label: "Vencidas sem desfecho", value: kpis.overdue || 0, hint: "registrar RJ, prorrogação ou sem RJ", tone: "danger" },
                { label: "Vencem em até 15 dias", value: kpis.due_15 || 0, hint: "a régua já está correndo", tone: "warning" },
                { label: "Viraram RJ no ano", value: kpis.rj_this_year || 0, hint: "desfecho RJ distribuída", tone: "success" },
                { label: "Lembretes na semana", value: kpis.sent_week || 0, hint: `${kpis.replied_week || 0} respondidos` },
            ];
        },
    },

    created() {
        this.load();
    },

    methods: {
        daysSince,
        formatDateTime,
        formatFeedDate,
        formatMoneyShort,

        formatShort(value) {
            const text = String(value || "");
            return text ? `${text.slice(8, 10)}/${text.slice(5, 7)}` : "";
        },

        async load() {
            this.loading = true;
            try {
                const [board, settings] = await Promise.all([axios.get("/api/admin/prospect/deadlines"), axios.get("/api/admin/prospect/settings")]);
                this.data = board.data.content;
                this.settings = settings.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        openCase(id) {
            this.$router.push({ path: `/prospeccao/casos/${id}`, query: { from: "cautelares" } });
        },

        openSettings() {
            const settings = this.settings || {};
            this.config = {
                open: true,
                saving: false,
                loadingAdmins: !this.admins.length,
                form: {
                    reminders_enabled: !!settings.reminders_enabled,
                    tca_days: settings.tca_days || 30,
                    reminder_hour: settings.reminder_hour || 8,
                    team_emails: [...(settings.team_emails || [])],
                    management_emails: [...(settings.management_emails || [])],
                },
            };
            if (!this.admins.length) this.loadAdmins();
        },

        // Administradores da plataforma (ja com e-mail) para os destinatarios.
        async loadAdmins() {
            try {
                const result = await axios.get("/api/admin/prospect/team");
                this.admins = result.data.content.filter((person) => person.email);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.config.loadingAdmins = false;
            }
        },

        async saveSettings() {
            this.config.saving = true;
            try {
                const form = this.config.form;
                const result = await axios.put("/api/admin/prospect/settings", {
                    reminders_enabled: form.reminders_enabled,
                    tca_days: Number(form.tca_days),
                    reminder_hour: Number(form.reminder_hour),
                    team_emails: form.team_emails,
                    management_emails: form.management_emails,
                });
                this.settings = result.data.content;
                this.config.open = false;
                notifySuccess(this, "Régua atualizada.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.config.saving = false;
            }
        },

        exportRows() {
            downloadXlsx(this.data.open.map((item) => ({
                Empresa: item.case.company_name,
                Processo: item.case.process_number,
                UF: item.case.uf || "",
                Passivo: item.case.amount,
                Vencimento: formatFeedDate(item.due_date),
                Situação: item.days_text,
                Base: `${item.base_label}${item.estimated ? " (estimado)" : ""}`,
                Dono: item.case.owner ? item.case.owner.name : "",
                "Último lembrete": item.last_reminder ? `${item.last_reminder.milestone_label} · ${item.last_reminder.reply_label || "sem resposta"}` : "",
                Pausado: item.reminders_paused ? "sim" : "",
            })), "cautelares-prazos");
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.pdl-stats {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    border: 1px solid $ui-border;
    border-radius: $ui-radius;
    background: $ui-bg;

    @media (max-width: 960px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

.pdl-stat {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 14px 18px;
    border-right: 1px solid $ui-border;

    &:last-child {
        border-right: 0;
    }
}

.pdl-stat__label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: $ui-muted;
}

.pdl-stat__value {
    font-size: 24px;
    font-weight: 600;
    color: $ui-fg;
}

.pdl-stat__hint {
    font-size: 12px;
    color: $ui-muted;
}

.pdl-notice {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    font-size: 13px;
    border-radius: $ui-radius;
    background: nth(map-get($ui-tones, warning), 1);
    color: nth(map-get($ui-tones, warning), 2);
}

.pdl-timeline__scale {
    display: grid;
    grid-template-columns: repeat(8, minmax(0, 1fr));
    margin-left: 34%;
    padding-bottom: 6px;
    font-size: 11px;
    color: $ui-muted;
    border-bottom: 1px solid $ui-border;
}

.pdl-row {
    display: grid;
    grid-template-columns: 34% minmax(0, 1fr);
    align-items: center;
    width: 100%;
    padding: 8px 0;
    border: 0;
    border-bottom: 1px solid $ui-border-soft;
    background: transparent;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: $ui-bg-muted;
    }
}

.pdl-row__name {
    padding-right: 12px;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pdl-row__track {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 22px;
}

.pdl-row__bar {
    display: block;
    height: 8px;
    border-radius: 999px;

    @each $tone, $colors in $ui-tones {
        &--#{$tone} {
            background: nth($colors, 3);
        }
    }
}

.pdl-row__label {
    font-size: 12px;
    white-space: nowrap;

    @each $tone, $colors in $ui-tones {
        &--#{$tone} {
            color: nth($colors, 2);
        }
    }
}

.pdl-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 16px;
    align-items: start;

    @media (max-width: 1100px) {
        grid-template-columns: minmax(0, 1fr);
    }
}

// Cartoes lado a lado: sem o espacamento vertical de ".ui-card + .ui-card".
.pdl-grid > .ui-card + .ui-card {
    margin-top: 0;
}

.pdl-rule,
.pdl-replies {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    td {
        padding: 8px 6px;
        border-bottom: 1px solid $ui-border-soft;
        vertical-align: top;
    }
}

.pdl-replies tr {
    cursor: pointer;

    &:hover td {
        background: $ui-bg-muted;
    }
}

.pdl-rule__key {
    width: 56px;
    font-weight: 600;
    color: $ui-accent;
}

.pdl-late {
    color: nth(map-get($ui-tones, danger), 2);
}
</style>
